import CancelledIcon from 'assets/tasks/cancelled.png';
import CompletedIcon from 'assets/tasks/completed.png';
import FailedIcon from 'assets/tasks/failed.png';
import InProgressIcon from 'assets/tasks/in-progress.png';
import OnHoldIcon from 'assets/tasks/on-hold.png';
import OutreachIcon from 'assets/tasks/outreach.png';
import RequestedIcon from 'assets/tasks/requested.png';
import type { Task } from 'fhir/r4';
import type { SemanticCOLORS } from 'semantic-ui-react';

export const OUTREACH_REASONS_OPTIONS = [
  {
    text: 'CAll BACK REQUESTED',
    key: 'call-back-requested',
    value: 'call back requested',
  },
  {
    text: 'DID NOT ANSWER/NO VM',
    key: 'did-not-answer/no-vm',
    value: 'did not answer/no vm',
  },
  {
    text: 'INCORRECT PHONE NUMBER',
    key: 'incorrect-phone-number',
    value: 'incorrect phone number',
  },
  {
    text: 'INTAKE SCHEDULED',
    key: 'intake-scheduled',
    value: 'intake scheduled',
  },
  { text: 'NO PHONE NUMBER', key: 'no-phone-number', value: 'no phone number' },
  { text: 'VOICEMAIL', key: 'voicemail', value: 'voicemail' },
];

export const REQUEST_OPTIONS = [
  { text: 'RESHIP', key: 'reship', value: 'reship' },
  { text: 'BONUS PACK', key: 'bonus-pack', value: 'bonus pack' },
  { text: 'OTHER', key: 'other', value: 'other' },
];

export const STATUS_COLORS: Record<string, SemanticCOLORS> = {
  requested: 'blue',
  'in-progress': 'yellow',
  completed: 'green',
  failed: 'red',
  cancelled: 'grey',
  'on-hold': 'orange',
  received: 'purple',
};

/**
 * The status dropdown's options. `satisfies` pins each `value` against
 * `Task['status']` — a typo in one of these codes is a compile error here rather
 * than a status the FHIR server rejects at runtime — while leaving the literal
 * types intact for the consumer that narrows the Dropdown's widened value back.
 */
export const STATUS_OPTIONS = [
  { text: 'REQUESTED', key: 'requested', value: 'requested', icon: RequestedIcon, color: STATUS_COLORS['requested'] },
  {
    text: 'IN-PROGRESS',
    key: 'in-progress',
    value: 'in-progress',
    icon: InProgressIcon,
    color: STATUS_COLORS['in-progress'],
  },
  { text: 'COMPLETED', key: 'completed', value: 'completed', icon: CompletedIcon, color: STATUS_COLORS['completed'] },
  { text: 'FAILED', key: 'failed', value: 'failed', icon: FailedIcon, color: STATUS_COLORS['failed'] },
  { text: 'CANCELLED', key: 'cancelled', value: 'cancelled', icon: CancelledIcon, color: STATUS_COLORS['cancelled'] },
  { text: 'ON-HOLD', key: 'paused', value: 'on-hold', icon: OnHoldIcon, color: STATUS_COLORS['on-hold'] },
  { text: 'OUTREACH', key: 'outreach', value: 'received', icon: OutreachIcon, color: STATUS_COLORS['received'] },
] satisfies readonly { text: string; key: string; value: Task['status']; icon: string; color: string | undefined }[];

export const STATUS_REASONS_OPTIONS = [
  { text: 'CHANGED INSURANCE', key: 'changed-insurance', value: 'changed insurance' },
  { text: 'DECEASED', key: 'deceased', value: 'deceased' },
  { text: 'DECLINED', key: 'Declined', value: 'declined' },
  {
    text: 'FOOD CARD ERROR',
    key: 'food-card-error',
    value: 'food card error',
  },
  {
    text: 'Food Dissatisfaction',
    key: 'food-dissatisfaction',
    value: 'food dissatisfaction',
  },
  { text: 'GATEKEEPER DECLINED', key: 'gatekeeper-declined', value: 'gatekeeper declined' },
  { text: 'HOSPITALIZED', key: 'Hospitalized', value: 'hospitalized' },
  { text: 'INSUFFICIENT FUNDS', key: 'insufficient-funds', value: 'insufficient funds' },
  { text: 'LOB Change', key: 'lob-change', value: 'lob change' },
  { text: 'LOCATION CHANGE', key: 'location-change', value: 'location change' },
  { text: 'LOST ELIGIBILITY', key: 'lost-eligibility', value: 'lost eligibility' },
  {
    text: 'MEMBER DIS-ENROLLED',
    key: 'Member Dis-enrolled',
    value: 'member dis-enrolled',
  },
  { text: 'NO PHONE NUMBER', key: 'no-phone-number', value: 'no phone number' },
  { text: 'NRx DIS-ENROLLED', key: 'NRx Dis-enrolled', value: 'nrx dis-enrolled' },
  {
    text: 'Personal Circumstances',
    key: 'personal-circumstances',
    value: 'personal circumstances',
  },
  { text: 'PROGRAM TRANSFER', key: 'program-transfer', value: 'program transfer' },
  { text: 'SILENT MEMBER', key: 'silent-member', value: 'silent member' },
  { text: 'UNREACHABLE', key: 'unreachable', value: 'unreachable' },
  { text: 'WRONG PHONE NUMBER', key: 'wrong-phone-number', value: 'wrong phone number' },
  { text: 'YEAR END', key: 'year-end', value: 'year end' },
];

export const CONSULT_TASK_SUB_TYPE_OPTIONS = [
  { text: '1:1', key: '1:1', value: '1:1' },
  { text: 'GROUP', key: 'group', value: 'group' },
  { text: 'FLEXIBLE', key: 'flexible', value: 'flexible' },
  { text: 'FFS-MNT - 1:1', key: 'ffs-mnt-1:1', value: 'ffs-mnt-1:1' },
];

/**
 * Group consult session titles keyed by curriculum category (Program Type).
 * Category is used only for sort order; it is not shown in the dropdown.
 */
export const CONSULT_TOPICS_BY_CATEGORY: Record<string, string[]> = {
  Diabetes: [
    'Holidays & Social Eating',
    'Lifestyle Levers: Sleep, Stress & Movement',
    'Protein & Plate Balance',
    'The 3 PM Energy Slump: Why It Happens with Diabetes and How to Beat It',
    'Understanding Your Blood Sugar Patterns',
  ],
  General: [
    'Breaking Down Ultra-Processed Foods: What to Know for Better Health',
    'Making the Most of the Food You Have',
    'More Fiber, Made Easy: Simple Ways to Get Enough Each Day',
    'Quick Meals When Time and Energy Are Low',
    'Sneaky Steps: Increasing Daily Movement for Better Health',
    'Stretching Food, Reducing Waste & Planning Ahead',
  ],
  'Heart Health': [
    'Eating for a Healthier Heart: Simple Change that Promote Heart Health',
    'Sleep, Stress & Your Heart: How Daily Habits Can Protect Your Cardiovascular Health',
    'The DASH Approach: Eating Patterns to Support a Healthier Blood Pressure',
    'Understanding Your Numbers: Cholesterol, Blood Pressure, and What to Eat',
  ],
  'Maternal Health': [
    'Feeding Your Baby: Breastfeeding, Formula, Pumping, and Beyond',
    'Fueling for Pregnancy & Postpartum Basics',
    'Nutrition for a Healthy Pregnancy: Preventing & Managing Common Pregnancy Complications',
    'Pregnancy Nutrition Essentials: Protein, Iron & Key Nutrients',
    'Preparing for Baby: Recovery, Self-Care & Infant Feeding Basics',
    'Sleep, Stress & Self-Care in the Perinatal Period',
  ],
};

/**
 * Builds a kebab-case option key from a consult topic title.
 * @param title - Session title shown in the consult-topic dropdown.
 */
const toConsultTopicKey = (title: string): string =>
  title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * Semantic UI select options for group consult topics, sorted by category
 * then alphabetically within each category (no category headers in the UI).
 */
export const CONSULT_TOPIC_OPTIONS = Object.keys(CONSULT_TOPICS_BY_CATEGORY)
  .sort((a, b) => a.localeCompare(b))
  .flatMap((category) =>
    [...CONSULT_TOPICS_BY_CATEGORY[category]!]
      .sort((a, b) => a.localeCompare(b))
      .map((title) => ({
        text: title,
        key: toConsultTopicKey(title),
        value: title,
      }))
  );
